/**
 * @description This implements the 3D scene with the scan and pointcloud.
 */

import * as THREE from 'three';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer';

import { AssetManager } from '../asset-manager';
import cameraManager from '../camera-manager';
import { PanoramaManager } from '../elements/panorama/panorama-manager';
import { PointCloudHandle } from '../geodata/point-clouds/point-cloud-handle';
// @ts-ignore
import { SunManager } from '../sun-manager';
import { Scene } from './scene';

export class SkyviewPanoramaScene extends Scene {
  private cameraUuid: string;

  private _panoramaManager: PanoramaManager;

  get panoramaManager() {
    return this._panoramaManager;
  }

  constructor() {
    super();

    this.cameraUuid = cameraManager().createFPSCamera(60, 1, 0.2, 20000);

    const camera = cameraManager().getCamera(this.cameraUuid);
    this._panoramaManager = new PanoramaManager(camera!.getTHREECamera());
  }

  async init(defaultTimelineContainerIndex?: string) {
    if (this.initialized) {
      return Promise.resolve();
    }

    this.initialized = true;
    return Promise.resolve();
  }

  activate(): Promise<void> {
    console.log('Activated panorama scene.');

    const activeCamera = cameraManager().setActiveCamera(this.cameraUuid, false);
    if (!activeCamera) {
      throw new Error(`The 3d scene camera does not exist: ${this.cameraUuid}`);
    }

    cameraManager().setFPSController();

    this.add(this._panoramaManager);

    return Promise.resolve();
  }

  deactivate() {
    console.log('Deactivated panorama scene');
    cameraManager().updateGeographicCoords();
    this.remove(this._panoramaManager);

    this.clearChildren(this as unknown as THREE.Mesh);
  }

  async animate() {
    const camera = cameraManager().getActiveCamera();
    AssetManager.instance.onAnimate(camera);

    return Promise.resolve();
  }

  render(renderer: THREE.WebGLRenderer, css2dRenderer?: CSS2DRenderer) {
    const skyviewCamera = cameraManager().getActiveCamera();
    if (!skyviewCamera) {
      throw new Error('Camera not defined.');
    }

    const camera = skyviewCamera.getTHREECamera();
    SunManager.instance.renderObjectWithLightSources(renderer, this, camera);

    // Render CSS2D objects (compass labels) if renderer is provided
    if (css2dRenderer) {
      css2dRenderer.render(this, camera);
    }
  }

  inactive() {
    /** */
  }

  clearData() {
    this.initialized = false;

    this.clearChildren(this as unknown as THREE.Mesh);

    const camera = cameraManager().getCamera(this.cameraUuid);
    if (camera) {
      this.remove(camera.getTHREECamera());
    }
  }

  clearCustom() {
    this.clearData();
    cameraManager().destroyCamera(this.cameraUuid);

    PointCloudHandle.instance.clearCustom();
  }

  isLoading() {
    return AssetManager.instance.isLoading();
  }
}
